import { getSessionAuthentication, baseUrl } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function legacyFetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(baseUrl + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Language {
    id: number;
    name: string;
    locale?: string;
}

export interface Localization {
    language: Language;
    name?: string;
}

export interface ShippingMethod {
    id: number;
    code: string;
    name: string;
    localizations: Localization[];
}

export async function getShippingMethod(id: number): Promise<ShippingMethod> {
    return legacyFetch<ShippingMethod>(`shippingmethods/${id}`);
}

export async function createShippingMethod(sm: Partial<ShippingMethod>): Promise<ShippingMethod> {
    return legacyFetch<ShippingMethod>('shippingmethods', {
        method: 'POST',
        body: JSON.stringify(sm),
    });
}

export async function updateShippingMethod(sm: ShippingMethod): Promise<ShippingMethod> {
    return legacyFetch<ShippingMethod>(`shippingmethods/${sm.id}`, {
        method: 'PUT',
        body: JSON.stringify(sm),
    });
}

export async function deleteShippingMethod(id: number): Promise<void> {
    await legacyFetch<void>(`shippingmethods/${id}`, { method: 'DELETE' });
}

export async function getLanguages(): Promise<Language[]> {
    return legacyFetch<Language[]>('shippingmethods/languages');
}
